export type Provider = 'stripe' | 'wise' | 'openai' | 'anthropic' | 'cloudflare' | 'resend';

type IntegrationGroupId = 'billing' | 'payments' | 'ai' | 'platform' | 'communication';

export interface IntegrationGroup {
  id: IntegrationGroupId;
  title: string;
  description: string;
  providers: Provider[];
}

export interface ProviderGuide {
  purpose: string;
  setupMethod: string;
  steps: string[];
  guidance: string[];
}

export const integrationGroups: IntegrationGroup[] = [
  {
    id: 'billing',
    title: 'Billing And Invoicing',
    description: 'Invoice generation and card payment collection.',
    providers: ['stripe'],
  },
  {
    id: 'payments',
    title: 'Bank Transfer Reconciliation',
    description: 'Wise transfer tracking and auto/manual invoice matching.',
    providers: ['wise'],
  },
  {
    id: 'ai',
    title: 'AI Providers',
    description: 'Model usage metering and cost accounting sources.',
    providers: ['openai', 'anthropic'],
  },
  {
    id: 'platform',
    title: 'Cloud Platform',
    description: 'Cloudflare account credentials for infrastructure usage data.',
    providers: ['cloudflare'],
  },
  {
    id: 'communication',
    title: 'Email Delivery',
    description: 'Invoice and payment lifecycle email delivery channel.',
    providers: ['resend'],
  },
];

export const providerGuides: Record<Provider, ProviderGuide> = {
  stripe: {
    purpose: 'Create payment links, receive card payments, and update invoice status via webhooks.',
    setupMethod: 'Use Stripe dashboard API keys and webhook signing secret.',
    steps: [
      'In Stripe Dashboard, open Developers -> API keys and copy the Secret Key.',
      'In Developers -> Webhooks, create endpoint /api/stripe/webhook and copy Signing Secret.',
      'Paste api_key and webhook_secret, click Save credentials, then Test connection.',
    ],
    guidance: [
      'Use restricted keys in production where possible.',
      'Ensure webhook endpoint is public and not blocked by auth middleware.',
    ],
  },
  wise: {
    purpose: 'Pull balances and transactions, and reconcile bank transfers with invoices.',
    setupMethod: 'Use Wise API token plus the profile ID of your business profile.',
    steps: [
      'In Wise business account, create a Personal API token with statement access.',
      'Use the Find profiles action here or call GET /v2/profiles with that API token.',
      'Copy the id from the profile where type is business.',
      'Paste api_token and profile_id, click Save credentials, then Test connection.',
    ],
    guidance: [
      'Wise officially calls this value profileId; for Chicross you usually want the business profile id.',
      'Configure webhook public key in integrations or worker secret for signature verification.',
      'Reference matching works best when bank transfer reference equals invoice payment_reference.',
    ],
  },
  openai: {
    purpose: 'Collect OpenAI usage/cost signals for billing and margin analysis.',
    setupMethod: 'Use organization admin key and org ID from OpenAI platform.',
    steps: [
      'Generate an Admin API Key in OpenAI platform (looks like sk-...).',
      'Copy your organization ID from OpenAI settings.',
      'Paste admin_key and org_id, then Save credentials and Test connection.',
    ],
    guidance: [
      'Keep admin keys scoped and rotated regularly.',
      'Use dedicated service credentials rather than personal accounts.',
      'Do not paste org_id into admin_key. admin_key should be an API key; org_id should start with org-.',
    ],
  },
  anthropic: {
    purpose: 'Collect Anthropic usage/cost signals for billing and margin analysis.',
    setupMethod: 'Use Anthropic admin API key.',
    steps: [
      'Create an admin API key in Anthropic Console.',
      'Paste admin_key in this panel.',
      'Save credentials and run Test connection.',
    ],
    guidance: [
      'Prefer a dedicated service workspace key for auditability.',
      'Rotate keys on schedule and after role changes.',
    ],
  },
  cloudflare: {
    purpose: 'Access Cloudflare account metadata for usage aggregation and operations integration.',
    setupMethod: 'Use account-scoped API token plus account ID.',
    steps: [
      'Create an API token in Cloudflare with required read scopes.',
      'Copy account_id from Cloudflare dashboard URL/account settings.',
      'Paste api_token and account_id, then Save credentials and Test connection.',
    ],
    guidance: [
      'Grant minimum required scopes only.',
      'Validate account_id matches the environment you are reconciling.',
    ],
  },
  resend: {
    purpose: 'Send invoice, reminder, and payment confirmation emails.',
    setupMethod: 'Use Resend API key and verified sender address/domain.',
    steps: [
      'Create API key in Resend dashboard.',
      'Verify sending domain/address and prepare from_address.',
      'Paste api_key and from_address, then Save credentials and Test connection.',
    ],
    guidance: [
      'from_address must belong to a verified domain in Resend.',
      'Use a mailbox monitored by finance/support for delivery failures.',
    ],
  },
};

export const providerGroupMap = new Map<Provider, IntegrationGroup>(
  integrationGroups.flatMap((group) =>
    group.providers.map((provider) => [provider, group] as const),
  ),
);
